import { jsx as _jsx } from "react/jsx-runtime";
import globalEventEmitter from '@modules/EventEmitter';
import { EVENTS } from '@modules/EventEmitter/const';
import { closeCreditModal, convertLevelName, EBillInterval, getBundleType, openCreditModal, useGlobalStore } from '@wegic/price-ui';
import Portal from '@wegic/ui/src/components/Portal';
import debounce from 'lodash-es/debounce';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import { updatePaymentMethodTrackingHandlers } from '@/helpers/payment/updatePaymentMethodTracking';
import { useUserStore } from '@/stores/user';
import { closeCreditPurchaseModal } from './helper';
import { collectCouponEvent, COUPON_EVENT, getCreditPurchaseSuccessTrackingContext, getCreditPurchaseTrackingContext, getPaymentFailCode, getPaymentFailReason, getUpgradeModalPaymentTrackingContext } from './tracking';
const CreditPurchaseModal = /*#__PURE__*/ lazy(()=>import('@wegic/price-ui').then((module)=>({
            default: module.CreditPurchaseModal
        })));
const CREDIT_PURCHASE_MODAL_PORTAL_CONTAINER_ID = 'global-credit-purchase-modal-portal';
const OPEN_DEBOUNCE_DELAY = 200;
export default function GlobalCreditPurchaseModal() {
    const visible = useGlobalStore((state)=>state.creditPurchaseModalVisible);
    const defaultSelectedCredits = useGlobalStore((state)=>state.creditModalDefaultSelectedCredits);
    const userSubscription = useUserStore((state)=>state.userSubscription);
    const trackingHandlersRef = useRef(null);
    const { billInterval, currentSubscribe } = useMemo(()=>{
        var _userSubscription_currentProduct_prices, _userSubscription_currentProduct;
        return {
            billInterval: (userSubscription === null || userSubscription === void 0 ? void 0 : (_userSubscription_currentProduct = userSubscription.currentProduct) === null || _userSubscription_currentProduct === void 0 ? void 0 : (_userSubscription_currentProduct_prices = _userSubscription_currentProduct.prices) === null || _userSubscription_currentProduct_prices === void 0 ? void 0 : _userSubscription_currentProduct_prices[0].billInterval) || EBillInterval.Month,
            currentSubscribe: getBundleType(convertLevelName(userSubscription === null || userSubscription === void 0 ? void 0 : userSubscription.currentProduct))
        };
    }, [
        userSubscription
    ]);
    const handleOpenCreditPurchaseModalEvent = useCallback(debounce((data)=>{
        const { source, defaultSelectedCredits, trackingHandlers } = data;
        if (useGlobalStore.getState().creditPurchaseModalVisible) {
            return;
        }
        trackingHandlersRef.current = trackingHandlers ?? null;
        collect('credit_purchase_modal_open', {
            source
        });
        openCreditModal(source, defaultSelectedCredits);
    }, OPEN_DEBOUNCE_DELAY), []);
    const handleCloseCreditPurchaseModalEvent = useCallback(()=>{
        handleOpenCreditPurchaseModalEvent.cancel();
        trackingHandlersRef.current = null;
        closeCreditModal();
    }, [
        handleOpenCreditPurchaseModalEvent
    ]);
    const handleCreditPurchaseSuccess = useCallback((data)=>{
        collect('pay_success', getCreditPurchaseSuccessTrackingContext(data));
    }, []);
    const handleCreditPurchaseFail = useCallback((data)=>{
        collect('pay_fail', {
            ...getCreditPurchaseTrackingContext(data),
            reason: getPaymentFailReason(data.error, data.errorToastKey),
            error_code: getPaymentFailCode(data.error)
        });
    }, []);
    const handleSubscriptionConfirmSuccess = useCallback((data)=>{
        collect('pay_success', getUpgradeModalPaymentTrackingContext(data, 'upgrade_modal'));
    }, []);
    const handleSubscriptionConfirmFail = useCallback((data)=>{
        collect('pay_fail', {
            ...getUpgradeModalPaymentTrackingContext(data, 'upgrade_modal'),
            reason: getPaymentFailReason(data.error, data.errorToastKey),
            error_code: getPaymentFailCode(data.error)
        });
    }, []);
    const handleCouponShow = useCallback((coupon)=>{
        collectCouponEvent(COUPON_EVENT.SHOW, 'credits_modal', coupon);
    }, []);
    const handleCouponCopy = useCallback((coupon)=>{
        collectCouponEvent(COUPON_EVENT.COPY, 'credits_modal', coupon);
    }, []);
    const defaultTrackingHandlers = useMemo(()=>({
            onCreditPurchaseSuccess: handleCreditPurchaseSuccess,
            onCreditPurchaseFail: handleCreditPurchaseFail,
            onSubscriptionConfirmSuccess: handleSubscriptionConfirmSuccess,
            onSubscriptionConfirmFail: handleSubscriptionConfirmFail,
            onCouponShow: handleCouponShow,
            onCouponCopy: handleCouponCopy,
            ...updatePaymentMethodTrackingHandlers
        }), [
        handleCouponCopy,
        handleCouponShow,
        handleCreditPurchaseFail,
        handleCreditPurchaseSuccess,
        handleSubscriptionConfirmFail,
        handleSubscriptionConfirmSuccess
    ]);
    // 打开方可能只覆盖积分或券埋点，默认订阅确认埋点仍需要保留给“升级 + 积分补买”组合单。
    const activeTrackingHandlers = trackingHandlersRef.current ? {
        ...defaultTrackingHandlers,
        ...trackingHandlersRef.current
    } : defaultTrackingHandlers;
    useEffect(()=>{
        globalEventEmitter.on(EVENTS.OPEN_CREDIT_PURCHASE_MODAL, handleOpenCreditPurchaseModalEvent);
        globalEventEmitter.on(EVENTS.CLOSE_CREDIT_PURCHASE_MODAL, handleCloseCreditPurchaseModalEvent);
        return ()=>{
            handleOpenCreditPurchaseModalEvent.cancel();
            globalEventEmitter.off(EVENTS.OPEN_CREDIT_PURCHASE_MODAL, handleOpenCreditPurchaseModalEvent);
            globalEventEmitter.off(EVENTS.CLOSE_CREDIT_PURCHASE_MODAL, handleCloseCreditPurchaseModalEvent);
        };
    }, [
        handleCloseCreditPurchaseModalEvent,
        handleOpenCreditPurchaseModalEvent
    ]);
    if (!visible) {
        return null;
    }
    return /*#__PURE__*/ _jsx(Portal, {
        id: CREDIT_PURCHASE_MODAL_PORTAL_CONTAINER_ID,
        children: /*#__PURE__*/ _jsx(Suspense, {
            fallback: null,
            children: /*#__PURE__*/ _jsx(CreditPurchaseModal, {
                visible: visible,
                billInterval: billInterval,
                currentSubscribe: currentSubscribe,
                defaultSelectedCredits: defaultSelectedCredits,
                onClose: closeCreditPurchaseModal,
                trackingHandlers: activeTrackingHandlers
            })
        })
    });
}
